// Thin wrapper over Capacitor so game code never imports native plugins
// directly. Every call is safe on the web, where it falls back or does nothing.
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";

export type Platform = "ios" | "android" | "web";

export const platform = Capacitor.getPlatform() as Platform;
export const isNative = Capacitor.isNativePlatform();

/** Hide native chrome once the first frame is ready. */
export async function ready(): Promise<void> {
  if (!isNative) return;
  await StatusBar.hide().catch(() => {});
  await SplashScreen.hide().catch(() => {});
}

/** Calls back when the app goes to the background or returns to the foreground. */
export function onActiveChange(cb: (active: boolean) => void): void {
  if (isNative) {
    void App.addListener("appStateChange", ({ isActive }) => cb(isActive));
    return;
  }
  document.addEventListener("visibilitychange", () => cb(document.visibilityState === "visible"));
}

export function tapFeedback(): void {
  if (isNative) void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}

/** Vibration for game feedback: native haptics (one impact per pattern), the browser's vibrate on the web. */
export function vibrate(pattern: number | number[]): void {
  if (isNative) {
    const strongest = Math.max(...(Array.isArray(pattern) ? pattern : [pattern]));
    const style = strongest >= 25 ? ImpactStyle.Heavy : strongest >= 12 ? ImpactStyle.Medium : ImpactStyle.Light;
    void Haptics.impact({ style }).catch(() => {});
    return;
  }
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Unsupported or not allowed (no user gesture yet).
  }
}
